import { CaretDownOutlined, CloseSquareOutlined } from '@ant-design/icons';
import { Select } from 'antd';
import { FunctionComponent, useEffect, useMemo, useState } from 'react';
import buildingIcon from '../../../assets/images/building-icon.png';
import { TransactionDataInfo } from '../models/transaction-data-info.model';
import TransactionDataCard from './TransactionDataCard';

interface Props {
    onTransactionClicked: (transaction: TransactionDataInfo) => void;
    allTransactionData: TransactionDataInfo[];
}

const SearchMenuPanel: FunctionComponent<Props> = ({ onTransactionClicked, allTransactionData }) => {
    const [isShowTipBox, setIsShowTipBox] = useState<boolean>(true),
        [selectedCountry, setSelectedCountry] = useState<string>(),
        [keyword, setKeyword] = useState<string>(''),
        [transactionData, setTransactionData] = useState<Array<TransactionDataInfo>>([]);

    const countries = useMemo(() => {
        const countrySet = new Set<string>();
        allTransactionData.forEach(item => {
            if (item.country) {
                countrySet.add(item.country);
            }
        });

        return Array.from(countrySet).sort((a, b) => a.localeCompare(b));
    }, [allTransactionData]);

    const applySearch = (searchKeyword: string, country?: string) => {
        let filtered = allTransactionData;

        if (country) {
            filtered = filtered.filter(
                item => item.country && item.country.toLowerCase() === country.toLowerCase()
            );
        }

        const normalizedKeyword = searchKeyword.trim();
        if (normalizedKeyword) {
            const split = normalizedKeyword.split(/\s+/);
            if (split.length === 2) {
                const lon = parseFloat(split[0]);
                const lat = parseFloat(split[1]);
                if (!isNaN(lon) && !isNaN(lat)) {
                    // Use tolerance to make coordinate matching user-friendly.
                    const tolerance = 0.0001;
                    filtered = filtered.filter(item =>
                        Math.abs((item.lon || 0) - lon) <= tolerance &&
                        Math.abs((item.lat || 0) - lat) <= tolerance
                    );
                }
            } else {
                const lowerKeyword = normalizedKeyword.toLowerCase();
                filtered = filtered.filter(item =>
                    [item.propertyName, item.address, item.city, item.state, item.country]
                        .filter((value): value is string => Boolean(value))
                        .some(value => value.toLowerCase().includes(lowerKeyword))
                );
            }
        }

        setTransactionData(filtered);
        setIsShowTipBox(false);
    };

    useEffect(() => {
        setTransactionData(allTransactionData);
    }, [allTransactionData]);

    const onSearch = () => {
        applySearch(keyword, selectedCountry);
    }

    const onInputKeyDown = (event) => {
        if (event.key === 'Enter') {
            onSearch();
        }
    }

    return (
        <>
            <div className='search-title'>Search our Transaction Data</div>
            <div className='search-description'>You can search property by address, coordinate and country.</div>
            <hr />
            <div className='bar'>
                <Select
                    showSearch
                    allowClear
                    suffixIcon={(<CaretDownOutlined />)}
                    className='country-select'
                    placeholder='Country...'
                    optionFilterProp='children'
                    value={selectedCountry}
                    onClear={() => setSelectedCountry(undefined)}
                    onChange={(value) => {
                        setSelectedCountry(value);
                        applySearch(keyword, value);
                    }}
                    filterOption={(input, option) =>
                        (option!.children as unknown as string).toLowerCase().includes(input.toLowerCase())
                    }
                >
                    {countries.map(country => (
                        <Select.Option key={country} value={country}>{country}</Select.Option>
                    ))}
                </Select>
                <input
                    className='searchbar'
                    type='text'
                    value={keyword}
                    onChange={(event) => setKeyword(event.target.value)}
                    onKeyDown={onInputKeyDown}
                    placeholder="coordinate search example: longitude latitude"
                />
                <span className='searchicon' onClick={onSearch}><svg focusable='false' xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24'><path d='M15.5 14h-.79l-.28-.27A6.471 6.471 0 0 0 16 9.5 6.5 6.5 0 1 0 9.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z'></path></svg></span>
            </div>

            <div className='search-result'>
                {transactionData?.map(building => (
                    <TransactionDataCard key={building.id} transactionData={building} onClicked={onTransactionClicked} />
                ))}
            </div>

            {!isShowTipBox ? null : (
                <div className='tip-box'>
                    <div className='close-icon' onClick={() => setIsShowTipBox(false)}>
                        <CloseSquareOutlined />
                    </div>
                    <img className='image' src={buildingIcon} />
                    <div className='text'>
                        Welcome to the Ascend-Invest transaction data.
                    </div>
                </div>
            )}
        </>
    );
};

export default SearchMenuPanel;
